import { useCallback, useMemo } from 'react'
import { useAllMaterials, useMaterialsByIds } from '@/hooks/useInventoryMaster'
import type { ApiMaterial } from '@/types/inventory'

// ─── Material catalog (adapter over the REAL Inventory module) ───────────────
//
// A process step consumes MATERIALS (Material ID + qty). The catalog is now the
// live inventory master data (`useMaterials`) — no more mock fixture. Requirements
// still store the material's id in the soft `consumableId` ref plus a denormalized
// `itemLabel`, so saved rows stay readable if the catalog moves.

export type MaterialType = 'consumable' | 'reagent' | 'kit'

export const MATERIAL_TYPE_LABELS: Record<MaterialType, string> = {
  consumable: 'Consumable',
  reagent: 'Reagent',
  kit: 'Kit',
}

export interface Material {
  id: string
  /** Primary line — the material's human key ("Material ID", e.g. MAT-000123). */
  label: string
  type: MaterialType
  /** Secondary line — the material code, then the name. */
  detail: string
}

export interface MaterialLot {
  /** The InventoryLot's DB id — what `returnInventory` (`ReturnLine.lotId`) actually names, distinct from `lot` (the human lot number). */
  id: string
  lot: string
  expiry: string | null
  /**
   * What this lot can still commit to a new step — `availableQuantity` when the
   * backend sends it, else the raw on-hand `quantity`.
   *
   * It is here so the allocator can fill a requirement's configured quantity
   * ITSELF rather than asking the operator to type it. Without a per-lot
   * ceiling the only way to spread a requirement across two lots is to have
   * someone key in the split, which is the entry this exists to remove.
   */
  available: number
  /**
   * The shelf count and what other in-flight steps are holding, kept beside
   * `available` so the row can say "85 of 100 available · 15 held by other
   * runs". `available` alone cannot distinguish a lot that is nearly spent from
   * one that is merely busy, and those call for different decisions.
   *
   * Optional because the backend has only sent them since availability was
   * added; a payload without them renders the bare `available` figure rather
   * than inventing a total.
   */
  quantity?: number
  reservedQuantity?: number
  /**
   * Mirrors `ApiLot.gateStatus`. Only `'quarantine'` gates a lot out of the
   * allocator (`offeredLots`/`selectableTotal` in `lib/process/allocation.ts`)
   * — the same rule the FEFO proposal already applies server-side.
   */
  gateStatus?: 'quarantine' | 'in_process' | 'available' | null
  /** Bin / shelf code within the storage zone, when the lot has been homed. */
  locationCode?: string | null
  /**
   * The storage position the lot is in, when it was put away in the hierarchy
   * — every lot received since, and which has no `locationCode`. Shown as the
   * full address (Facility › … › Room › unit › slot) through `useLocationText`.
   */
  storagePath?: string | null
}

const EMPTY: ApiMaterial[] = []

/**
 * Map a material onto the process picker's 3 buckets.
 *
 * A kit is a material PRODUCED by completing a kit build (`kitFormFamilyId`),
 * not one whose free-text type happens to read "kit". Matching on the string
 * put hand-authored placeholders in the Kit tab, so the tab advertised kits
 * that had never been assembled and could never be picked.
 */
function deriveType(m: ApiMaterial): MaterialType {
  if (m.kitFormFamilyId) return 'kit'
  const t = m.materialType.toLowerCase()
  if (t.includes('reagent') || t.includes('media')) return 'reagent'
  return 'consumable'
}

/**
 * A Kit-typed material with no kit form behind it — a hand-authored leftover
 * from before kits were produced by building them. Not selectable, but still
 * resolvable so a requirement already referencing one keeps rendering.
 */
export function isLegacyKitMaterial(m: ApiMaterial): boolean {
  return !m.kitFormFamilyId && m.materialType.toLowerCase().includes('kit')
}

function toMaterial(m: ApiMaterial): Material {
  return {
    id: m.id,
    // Material ID is the human key someone actually looks a material up by, so
    // it leads. Falls back to the code only so the row can never render blank.
    label: m.materialId || m.materialCode,
    type: deriveType(m),
    // Code first, then the name. The code stays visible (and searchable — the
    // picker's filter matches on `detail`) now that it no longer leads.
    detail: [m.materialCode, m.materialName].filter(Boolean).join(' · '),
  }
}

function toLots(m: ApiMaterial): MaterialLot[] {
  return (m.lots ?? []).map((l) => ({
    id: l.id,
    lot: l.lotNumber,
    expiry: l.expiryDate ?? null,
    // `?? l.quantity` rather than `|| l.quantity`: a lot that is fully reserved
    // has availableQuantity 0, and falling back there would offer its whole
    // on-hand as free.
    available: l.availableQuantity ?? l.quantity ?? 0,
    quantity: l.quantity,
    reservedQuantity: l.reservedQuantity,
    gateStatus: l.gateStatus ?? null,
    locationCode: l.locationCode ?? null,
    storagePath: l.storageNodePath ?? null,
  }))
}

/**
 * Live inventory adapted to the process material catalog: the pickable materials,
 * a lookup, and each material's real lots (from the inventory lot records).
 */
export function useProcessMaterials() {
  // The full catalog — `useAllMaterials` pages past the backend's 200-row cap so
  // `materialById`/`lotsFor` can resolve materials beyond the first page.
  const { data, isLoading } = useAllMaterials()
  const items = data?.items ?? EMPTY
  // Pickable list excludes legacy hand-made kits; the lookup keeps ALL of them
  // so an existing requirement pointing at one still renders its label.
  const materials = useMemo(
    () => items.filter((m) => !isLegacyKitMaterial(m)).map(toMaterial),
    [items],
  )
  const materialById = useMemo(() => new Map(items.map((m) => [m.id, toMaterial(m)])), [items])
  const rawById = useMemo(() => new Map(items.map((m) => [m.id, m])), [items])
  const lotsFor = useCallback(
    (id: string): MaterialLot[] => {
      const m = rawById.get(id)
      return m ? toLots(m) : []
    },
    [rawById],
  )
  return { materials, materialById, lotsFor, isLoading }
}

/**
 * The lots of just these materials (z8nrz7e8kw): what a step being worked
 * needs — the materials it is configured with, the lots already allocated —
 * without downloading the catalogue. For any of these ids `lotsFor` answers
 * exactly as `useProcessMaterials`' does (same rows, same adaptation); for any
 * other it returns [], as that one does for an unknown id.
 */
export function useProcessMaterialLots(consumableIds: readonly string[]) {
  const { data } = useMaterialsByIds(consumableIds)
  const rawById = useMemo(() => new Map((data?.items ?? EMPTY).map((m) => [m.id, m])), [data])
  const lotsFor = useCallback(
    (id: string): MaterialLot[] => {
      const m = rawById.get(id)
      return m ? toLots(m) : []
    },
    [rawById],
  )
  return { lotsFor }
}
